import React, { useState } from "react";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AddSubRoles } from "../../redux/Actions/userStatusService";
import customSuccessAlert from "../../Alerts/customSuccessAlert";
import customFailAlert from "../../Alerts/customFailAlert";

function AddUserForm() {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.userStatus);
  
  const [formData, setFormData] = useState({
    email: "",
    entityName: "",
    entityType: "MANAGER",
    role: "MANAGER"
  });
  
  const [errors, setErrors] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null);

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Entity name validation
    if (!formData.entityName.trim()) {
      newErrors.entityName = "Entity name is required";
    } else if (formData.entityName.trim().length < 2) {
      newErrors.entityName = "Entity name must be at least 2 characters long";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitStatus("loading");
      
      // Prepare payload exactly as requested
      const payload = {
        email: formData.email,
        role: "MANAGER",
        entityInfo: {
          name: formData.entityName,
          entityType: "MANAGER"
        }
      };

      const response = await dispatch(AddSubRoles(payload));

      if (response?.payload?.statusCode === 201) {
        setSubmitStatus("success");
        customSuccessAlert("User added successfully!");
        
        // Reset form
        setFormData({
          email: "",
          entityName: "",
          entityType: "MANAGER",
          role: "MANAGER"
        });
        setErrors({});
      } else {
        setSubmitStatus("error");
        customFailAlert(response?.payload?.message || "Failed to add user");
      }
    } catch (error) {
      setSubmitStatus("error");
      customFailAlert(error?.message || "An error occurred while adding user");
    }
  };

  const handleReset = () => {
    setFormData({
      email: "",
      entityName: "",
      entityType: "MANAGER",
      role: "MANAGER"
    });
    setErrors({});
    setSubmitStatus(null);
  };

  return (
    <Paper elevation={1} sx={{ p: 3, minHeight: '500px' }}>
      <Typography variant="h6" sx={{ mb: 3, color: "#18275A", fontWeight: 600 }}>
        Add New Manager
      </Typography>
      
      {submitStatus === "success" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          User has been added successfully!
        </Alert>
      )}
      
      {submitStatus === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to add user. Please try again.
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              variant="outlined"
              required
              error={!!errors.email}
              helperText={errors.email || "Enter the user's email address"}
              placeholder="user@example.com"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Entity Name"
              name="entityName"
              value={formData.entityName}
              onChange={handleInputChange}
              variant="outlined"
              required
              error={!!errors.entityName}
              helperText={errors.entityName || "Enter the entity or company name"}
              placeholder="Enter entity name"
            />
          </Grid>

        </Grid>

        <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || submitStatus === "loading"}
            sx={{ 
              minWidth: 120,
              backgroundColor: "#3d76d2",
              "&:hover": {
                backgroundColor: "#0f1a3d"
              }
            }}
          >
            {loading || submitStatus === "loading" ? "Adding..." : "Add Manager"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={loading || submitStatus === "loading"}
          >
            Reset
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}

export default AddUserForm; 